import Button from '@ui/Button';
import Input from '@ui/Input';
import React from 'react';
import * as yup from 'yup';
import { Controller, useForm } from 'react-hook-form';
import { yupResolver } from '@hookform/resolvers/yup';
import Typography from '@ui/Typography';
import Select from '@ui/Select';
import { getAllISOCodes } from 'iso-country-currency';
import { defaults, pick, remove, sortBy } from 'lodash-es';
import { getCountryName } from '@utils/stringHelpers';
import { getUserCountry } from '@/utils/user.helper';

const schema = yup.object().shape({
  country: yup
    .object()
    .required('Please select a country')
    .typeError('Please select a country'),
  zipCode: yup
    .string()
    .min(5, 'Zip code must be at least 5 characters long')
    .max(10, 'Zip code must be at most 10 characters long')
    .notRequired(),
  linkedinProfile: yup
    .string()
    .max(100, 'Linkedin profile must be at most 100 characters long')
    .nullable()
    .notRequired(),
});

const getCountries = () => {
  const countries = sortBy(
    getAllISOCodes().map((country) => ({
      label: country.countryName,
      id: country.iso,
    })),
    'label'
  );

  const priorityCountries = remove(countries, (country) =>
    ['US', 'IN', 'MX'].includes(country.id)
  );

  return [
    priorityCountries.find((c) => c.id === 'US'),
    priorityCountries.find((c) => c.id === 'IN'),
    priorityCountries.find((c) => c.id === 'MX'),
    ...countries,
  ];
};

function EditProfileLocationStep(props) {
  const { onSubmit, onBack, onNext, formData, userProfile } = props;

  const countries = getCountries();
  const selectedCountry = countries.find(
    (country) => country.id === getUserCountry(userProfile)
  );

  const defaultValues = defaults(
    pick(formData, ['country', 'zipCode', 'linkedInProfile']),
    {
      linkedInProfile: userProfile ? userProfile.linkedin_profile : '',
      zipCode: userProfile ? userProfile.zip_code : '',
      country: selectedCountry || null,
    }
  );

  const form = useForm({
    defaultValues: defaultValues,
    mode: 'onChange',
    resolver: yupResolver(schema),
  });

  const {
    control,
    handleSubmit,
    formState: { errors, isValid },
    watch,
  } = form;

  const handleFormSubmit = (data) => {
    onSubmit(data);
  };

  return (
    <div className="flex flex-col gap-12 flex-1 w-full">
      {/* <Typography variant="dialogtitle">Set Up Your Account</Typography> */}

      <form
        onSubmit={handleSubmit(handleFormSubmit)}
        className="flex flex-col gap-6">
        <Controller
          name="country"
          control={control}
          render={({ field: { ref, ...rest } }) => (
            <Select
              label="Country"
              placeholder="Country of residence"
              required
              options={countries}
              {...rest}
              error={errors.country?.message}
            />
          )}
        />

        <Controller
          name="zipCode"
          control={control}
          render={({ field: { ref, ...rest } }) => (
            <Input
              label="zipCode"
              required
              {...rest}
              error={errors.zipCode?.message}
            />
          )}
        />

        <Controller
          name="linkedInProfile"
          control={control}
          render={({ field: { ref, ...rest } }) => (
            <Input label="LinkedIn Profile " {...rest} />
          )}
        />

        <div className="flex justify-between flex-rows gap-4">
          <div className="flex-flow-col">
            <Button
              variant="outlined"
              color="primary"
              className="mt-2"
              onClick={onBack}>
              Back
            </Button>
          </div>

          {isValid ? (
            <div className="flex-flow-col">
              <Button
                variant="filled"
                color="primary"
                type="submit"
                className="mt-2"
                onClick={onNext}>
                Next
              </Button>
            </div>
          ) : (
            <div className="flex-flow-col">
              <Button
                disabled
                variant="outlined"
                color="primary"
                type="submit"
                className="mt-2">
                Next
              </Button>
            </div>
          )}
        </div>
      </form>
    </div>
  );
}

export default EditProfileLocationStep;
